import React, { useState, useEffect, useCallback, useMemo } from "react";
import scriptjs from "scriptjs";
import PropTypes from "prop-types";
import {
  Conference,
  Publisher,
  Attendee,
  Attendees
} from "@teladoc/conference-react";
import { constants } from "../resource/constants";
import { useDialog } from "../context/messaging-layer";
import { devWarn } from "../utils/dev-logger";

const PUBLISHERS_KEY = Symbol("publishersKey");

const videoStylesToInject = {
  __html: `
#cf-video-container {
  position: relative;
  flex: 1;
}
#cf-video-container, .cf-attendee, #cf-video-container--attendees {
  height: 100%;
}
#cf-video-container--host {
  position: absolute;
  bottom: 0;
  right: 0;
  height: 30%;
  width: 30%;
  z-index: 2;
}`
};

const Video = React.memo(
  ({
    config,
    publishAudio,
    onStreamCreated,
    onPublished,
    attendees,
    setIsProviderConected
  }) => (
    <Conference config={config} onStreamCreated={onStreamCreated}>
      <Attendees>
        <Publisher
          publishAudio={publishAudio}
          onPublished={onPublished}
          properties={{ style: { buttonDisplayMode: "off" } }}
        />
        {attendees.map((attendee, index) => {
          if (!attendee || attendee.destroyed) return null;

          const { streamId } = attendee;
          const key = `${streamId}_${index}`;
          const { userType } = JSON.parse(attendee.connection.data);
          return (
            <Attendee
              onDestroyed={() => {
                setIsProviderConected(false);
              }}
              onSubscribed={() => {
                setIsProviderConected(true);
              }}
              key={key}
              stream={attendee}
              userType={userType}
            />
          );
        })}
      </Attendees>
    </Conference>
  )
);

function VideoControls({
  isMuted,
  onToggleAudio,
  onEnd,
  onUpload,
  components,
  appData: { settings },
  isAddImagesScreen = false
}) {
  const { Button } = components;

  return (
    <div>
      <Button
        onClick={() => onToggleAudio(!isMuted)}
        label={
          isMuted ? settings.translations.unmute : settings.translations.mute
        }
        className={`muteButton ${isMuted ? "muted" : ""}`}
      />
      <Button
        onClick={onEnd}
        label={settings.translations.end}
        className='endButton'
      />
      {isAddImagesScreen && (
        <Button
          onClick={onUpload}
          label={settings.translations.upload}
          className='uploadButton'
        />
      )}
    </div>
  );
}

export function VideoRoomComponent({
  loadingPlaceholder,
  config,
  components,
  appData,
  onClose,
  setOnExitVideoRoomHandler,
  addImageScreen,
  statusPollingConfig
}) {
  const { showDialog } = useDialog();
  const [isOTLoaded, setIsOTLoaded] = useState(() => typeof OT !== "undefined");
  const [isMuted, setIsMuted] = useState(false);
  const [attendees, setAttendees] = useState([]);
  const videoConfig = useMemo(() => ({ ...config, role: "member" }), []);
  const [isProviderConected, setIsProviderConected] = useState(false);

  const consultStatusPollingEnable = statusPollingConfig?.enable;
  const consultStatusPollingInterval =
    statusPollingConfig?.intervalForeground * 1000 ?? 20000;
  const consultStatusText = appData?.consultStatus?.status_text;

  useEffect(() => {
    if (consultStatusPollingEnable) {
      appData.updateConsultState();
      const consultStatusPollingTimer = setInterval(
        appData.updateConsultState,
        consultStatusPollingInterval
      );

      return () => clearTimeout(consultStatusPollingTimer);
    }
  }, []);

  useEffect(() => {
    if (!isOTLoaded) {
      scriptjs(constants.openTokUrl, () => setIsOTLoaded(true));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (window[PUBLISHERS_KEY]) {
        window[PUBLISHERS_KEY].forEach(publisher => {
          if (publisher?.id) {
            publisher.session.destroy();
            publisher.destroy();
          }
        });
      }
    };
  }, []);

  const onStreamCreated = useCallback(
    attendee => {
      setAttendees(prevAttendees => [...prevAttendees, attendee]);
    },
    [setAttendees]
  );

  const onStreamPropertyChanged = event => {
    if (event.changedProperty === "hasAudio") {
      setIsMuted(!event.newValue);
    }
  };

  const bindCustomPublisherEvents = provider => {
    try {
      provider.session.on({
        streamPropertyChanged: onStreamPropertyChanged
      });
    } catch (error) {
      devWarn("Could not bind events to publisher!");
    }
  };

  const onPublished = useCallback(({ publisher, props: { provider } }) => {
    bindCustomPublisherEvents(provider);
    if (!window[PUBLISHERS_KEY]) {
      window[PUBLISHERS_KEY] = [];
    }
    window[PUBLISHERS_KEY].push(publisher);
  }, []);

  const handleEndCall = useCallback(
    onYes => {
      showDialog({
        actions: [
          { label: appData.settings.translations.no },
          {
            label: appData.settings.translations.yes,
            onClick: () => {
              appData.consultStatus = {};
              if (window[PUBLISHERS_KEY]) {
                window[PUBLISHERS_KEY].forEach(publisher => {
                  if (publisher?.id) {
                    publisher.session.destroy();
                    publisher.destroy();
                    if (appData.isOneApp) window.location = "/";
                  }
                });
              }
              onYes();
            }
          }
        ],
        content: {
          title: appData.settings.translations.are_you_sure_you_want_to_leave
        }
      });
    },
    [showDialog]
  );

  setOnExitVideoRoomHandler(handleEndCall);

  const handleImageUpload = useCallback(() => {
    showDialog({
      content: {
        modalScreen: true,
        screen: addImageScreen,
        withClose: true
      }
    });
  }, []);

  const isAddImagesScreen = !!addImageScreen;

  return isOTLoaded ? (
    <>
      {/* eslint-disable-next-line react/no-danger */}
      <style dangerouslySetInnerHTML={videoStylesToInject} />
      {!isProviderConected && consultStatusText}
      <div
        className='websdk_video_room_component_wrapper'
        style={{
          width: "100%",
          height: "50vh",
          display: "flex",
          justifyContent: "space-between"
        }}
      >
        <Video
          onStreamCreated={onStreamCreated}
          onPublished={onPublished}
          publishAudio={!isMuted}
          attendees={attendees}
          config={videoConfig}
          setIsProviderConected={setIsProviderConected}
        />
        <VideoControls
          components={components}
          appData={appData}
          onToggleAudio={setIsMuted}
          onEnd={() => handleEndCall(onClose)}
          onUpload={handleImageUpload}
          isMuted={isMuted}
          isAddImagesScreen={isAddImagesScreen}
        />
      </div>
    </>
  ) : (
    loadingPlaceholder || appData.settings.translations.loading_video_room
  );
}

const configPropType = PropTypes.shape({
  apiKey: PropTypes.string.isRequired,
  provider: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  jwt: PropTypes.string.isRequired
});

VideoRoomComponent.propTypes = {
  loadingPlaceholder: PropTypes.node,
  config: configPropType.isRequired,
  appData: PropTypes.objectOf(PropTypes.any),
  components: PropTypes.shape({
    Button: PropTypes.oneOfType([PropTypes.func, PropTypes.element]).isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  addImageScreen: PropTypes.objectOf(PropTypes.object),
  setOnExitVideoRoomHandler: PropTypes.func.isRequired,
  statusPollingConfig: PropTypes.shape({
    intervalForeground: PropTypes.number,
    enable: PropTypes.bool
  })
};

VideoControls.propTypes = {
  isMuted: PropTypes.bool.isRequired,
  onToggleAudio: PropTypes.func.isRequired,
  onEnd: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  components: PropTypes.shape({
    Button: PropTypes.oneOfType([PropTypes.func, PropTypes.element]).isRequired
  }).isRequired,
  appData: PropTypes.objectOf(PropTypes.object),
  isAddImagesScreen: PropTypes.bool.isRequired
};

Video.displayName = "Video";
Video.propTypes = {
  config: configPropType.isRequired,
  publishAudio: PropTypes.bool.isRequired,
  onStreamCreated: PropTypes.func.isRequired,
  onPublished: PropTypes.func.isRequired,
  attendees: PropTypes.arrayOf(PropTypes.object).isRequired,
  setIsProviderConected: PropTypes.func.isRequired
};
